import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Upload, Download, Pencil, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, download, fmtQty, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Empty, Field, tableWrap, th, td, num } from "@/components/common";

const BLANK = { sku: "", name: "", cable_size: "", cores: "", category: "", cable_type: "", conductor: "", armour: "", voltage: "", brand: "", hsn: "8544", gst_rate: 18, uom: "m", purchase_cost: 0, selling_price: 0, min_stock: 0, warehouse: "", rack: "", status: "active" };

function Sel({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder || "Select"} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const { lookups, can } = useAuth();
  const [f, setF] = useState(initial || BLANK);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    try {
      const body = { ...f, gst_rate: +f.gst_rate, purchase_cost: +(f.purchase_cost || 0), selling_price: +f.selling_price, min_stock: +f.min_stock };
      if (initial?.id) await api.put(`/products/${initial.id}`, body); else await api.post("/products", body);
      toast.success("Product saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const L = lookups || {};
  const inp = (k, label, props = {}) => <Field label={label} required={props.required}><Input value={f[k] ?? ""} onChange={set(k)} data-testid={`product-${k}-input`} {...props} /></Field>;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {inp("sku", "SKU", { required: true })}
          <Field label="Brand" required><Sel value={f.brand} onChange={set("brand")} options={L.brands || []} testid="product-brand-select" /></Field>
          {inp("cable_size", "Cable Size", { required: true, placeholder: "240 sq mm" })}
          {inp("cores", "No. of Cores", { placeholder: "3.5" })}
          <Field label="Category" required><Sel value={f.category} onChange={set("category")} options={L.categories || []} testid="product-category-select" /></Field>
          {inp("cable_type", "Cable Type", { placeholder: "XLPE / PVC / FRLS" })}
          <Field label="Conductor"><Sel value={f.conductor} onChange={set("conductor")} options={L.conductors || []} testid="product-conductor-select" /></Field>
          <Field label="Armour Type"><Sel value={f.armour} onChange={set("armour")} options={L.armour_types || []} testid="product-armour-select" /></Field>
          <Field label="Voltage Grade"><Sel value={f.voltage} onChange={set("voltage")} options={L.voltage_grades || []} testid="product-voltage-select" /></Field>
          {inp("name", "Product Name (auto if blank)")}
          {inp("hsn", "HSN Code")}
          {inp("gst_rate", "GST Rate %", { type: "number" })}
          <Field label="Unit" required><Sel value={f.uom} onChange={set("uom")} options={L.units || ["m"]} testid="product-uom-select" /></Field>
          {can("cost:view") && inp("purchase_cost", "Purchase Cost (₹/unit)", { type: "number", step: "0.01" })}
          {inp("selling_price", "Selling Price (₹/unit)", { type: "number", step: "0.01" })}
          {inp("min_stock", "Minimum Stock Level", { type: "number" })}
          <Field label="Warehouse"><Sel value={f.warehouse} onChange={set("warehouse")} options={L.warehouses || []} testid="product-warehouse-select" /></Field>
          {inp("rack", "Rack Location")}
          <Field label="Status"><Sel value={f.status} onChange={set("status")} options={["active", "inactive"]} testid="product-status-select" /></Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="product-save-btn">Save product</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { can, lookups } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [edit, setEdit] = useState(null);
  const [sel, setSel] = useState([]);
  const fileRef = useRef();
  const canDel = can("products:delete");
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const del = async () => {
    if (!window.confirm(`Delete ${sel.length} product(s)? Products that already have stock will be kept.`)) return;
    try {
      const { data } = await api.post("/products/delete", { ids: sel });
      toast.success(`${data.deleted} product(s) deleted`);
      if (data.blocked.length) toast.error(`Kept (have stock): ${data.blocked.slice(0, 5).join(", ")}${data.blocked.length > 5 ? "…" : ""}`);
      setSel([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const load = () => api.get("/products", { params: { q, brand: brand === "all" ? "" : brand } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q, brand]); // eslint-disable-line react-hooks/exhaustive-deps

  const importFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/products/import", fd);
      toast.success(`${data.created} created, ${data.skipped.length} duplicate SKUs skipped, ${data.errors.length} errors`);
      data.errors.slice(0, 5).forEach((er) => toast.error(`Row ${er.row}: ${er.error}`));
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  return (
    <div>
      <PageHeader title="Product Master" subtitle="Every cable SKU by brand, size, type and unit. SKUs are unique.">
        <Button variant="outline" onClick={() => download("/products/template.xlsx", "Roxy_Product_Import_Template.xlsx")} data-testid="product-template-btn"><Download className="h-4 w-4 mr-1" />Template</Button>
        <Button variant="outline" onClick={() => download("/products/export.xlsx", "Roxy_Product_Master.xlsx")} data-testid="product-export-btn"><Download className="h-4 w-4 mr-1" />Export</Button>
        {can("products:write") && <>
          <input type="file" accept=".xlsx" hidden ref={fileRef} onChange={importFile} data-testid="product-import-input" />
          <Button variant="outline" onClick={() => fileRef.current.click()} data-testid="product-import-btn"><Upload className="h-4 w-4 mr-1" />Import Excel</Button>
          <Button onClick={() => setEdit(BLANK)} data-testid="product-add-btn"><Plus className="h-4 w-4 mr-1" />Add product</Button>
        </>}
        {canDel && <Button variant="outline" className="sm:hidden" onClick={() => setSel(sel.length === items.length ? [] : items.map((p) => p.id))} data-testid="product-select-all-mobile">{sel.length === items.length && items.length ? "Clear selection" : "Select all"}</Button>}
        {canDel && sel.length > 0 && <Button variant="destructive" onClick={del} data-testid="product-delete-selected-btn"><Trash2 className="h-4 w-4 mr-1" />Delete {sel.length}</Button>}
      </PageHeader>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input placeholder="Search SKU, name, size, type…" value={q} onChange={(e) => setQ(e.target.value)} className="sm:max-w-sm" data-testid="product-search-input" />
        <Select value={brand} onValueChange={setBrand}>
          <SelectTrigger className="sm:w-48" data-testid="product-brand-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All brands</SelectItem>{(lookups?.brands || []).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className={tableWrap}>
        <table className="w-full" data-testid="products-table">
          <thead><tr>{canDel && <th className={th}><Checkbox checked={items.length > 0 && sel.length === items.length} onCheckedChange={(v) => setSel(v ? items.map((p) => p.id) : [])} data-testid="product-select-all" /></th>}{["SKU", "Product", "Brand", "Size", "Cores", "Category", "Unit", "GST", ...(can("cost:view") ? ["Cost"] : []), "Price", "Min", "Status", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody>{items.map((p) => (
            <tr key={p.id} data-testid={`product-row-${p.sku}`} className="hover:bg-muted/40">
              {canDel && <td className={td}><Checkbox checked={sel.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`product-select-${p.sku}`} /></td>}
              <td className={`${td} font-mono font-medium`}>{p.sku}</td><td className={td}>{p.name}</td><td className={td}>{p.brand}</td><td className={td}>{p.cable_size}</td>
              <td className={td}>{p.cores}</td><td className={td}>{p.category}</td><td className={td}>{p.uom}</td><td className={`${td} ${num}`}>{p.gst_rate}%</td>
              {can("cost:view") && <td className={`${td} ${num}`}>{fmtINR(p.purchase_cost)}</td>}
              <td className={`${td} ${num}`}>{fmtINR(p.selling_price)}</td><td className={`${td} ${num}`}>{fmtQty(p.min_stock, p.uom)}</td>
              <td className={td}><StatusBadge status={p.status} /></td>
              <td className={td}>{can("products:write") && <Button size="icon" variant="ghost" onClick={() => setEdit(p)} data-testid={`product-edit-${p.sku}`}><Pencil className="h-4 w-4" /></Button>}</td>
            </tr>
          ))}</tbody>
        </table>
        {!items.length && <Empty text="No products yet. Add one or import from Excel." />}
      </div>
      {edit && <ProductForm initial={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
